import {
  GridRowModes,
  GridRowModesModel,
  GridToolbarContainer,
} from "@mui/x-data-grid";
import ActionButtons from "../ActionUserButtons";
import { API_URL, handleSendMessage, syncItems } from "src/utilities";
import { useMutation } from "@tanstack/react-query";
import AddIcon from "@mui/icons-material/Add";

import { useAuth } from "src/context/auth";
import { useNotifications } from "@toolpad/core/useNotifications";
import { Box, Button, Stack } from "@mui/material";
import { IAppUser } from "src/interface/IUser";
import { IUserInfo } from "src/interface/IUserInfo";
import { TypeUserInfo } from "src/interface/enum/TypeUserInfo";

interface EditToolbarUserInfoProps {
  userRef?: string;
  setRows: (newRows: (oldRows: IUserInfo[]) => IUserInfo[]) => void;
  setRowModesModel: (
    newModel: (oldModel: GridRowModesModel) => GridRowModesModel
  ) => void;
  onClose: () => void;
  rows: IUserInfo[];
  updatingUserInfo?: IUserInfo[];
  setUpdatingUserInfo?: (updatingUserInfo: IUserInfo[]) => void;
  existingUserInfo?: IUserInfo[];
  refetchUser: () => void;
}

export function EditToolbarUserInfo({
  rows,
  setRowModesModel,
  setRows,
  onClose,
  setUpdatingUserInfo,
  existingUserInfo,
  updatingUserInfo,
  userRef,
  refetchUser,
}: EditToolbarUserInfoProps) {
  const { user } = useAuth();
  const handleClick = () => {
    const userID = rows ? rows.length + 1 : 1; // ایجاد یک شناسه منحصر به فرد برای ردیف جدید
    const newRow: IUserInfo = {
      id: userID,
      caption: "",
      createId: "",
      type: TypeUserInfo.Address,
      postalCode: "",

      isNew: true, // نشان‌دهنده ردیف جدید
    };

    setRows((oldRows) => [...(oldRows || ([] as IUserInfo[])), newRow]);

    setRowModesModel((oldModel) => ({
      ...oldModel,
      [userID]: { mode: GridRowModes.Edit, fieldToFocus: "itemRef" },
    }));
  };
  const notifications = useNotifications();

  const { mutateAsync: insertUserInfoAsync, isPending: userInfoLoading } =
    useMutation({
      mutationFn: async (body: any) => {
        const response = await fetch(API_URL + "user-info", {
          method: "POST",
          headers: {
            Accept: "*/*",
            "Content-Type": "application/json-patch+json",
            Authorization: `Bearer ${user?.token}`,
          },
          body: JSON.stringify(body),
        });
        return await response.json();
      },
      onSuccess: (responseData) => {
        handleSendMessage(
          notifications,
          "راه ارتباطی جدید اضافه شد!",
          "success",
          3000,
          "insert"
        );
        JSON.stringify(responseData);
        refetchUser();
      },
      onError: (error) => {
        handleSendMessage(
          notifications,
          "خطا در عملیات افزودن راه های ارتباطی!",
          "error",
          3000,
          "error"
        );
        console.log(error);
      },
    });
  const {
    mutateAsync: deleteUserInfoAsync,
    isPending: isDeleteUserInfoLoading,
  } = useMutation({
    mutationFn: async (body: any) => {
      const response = await fetch(API_URL + "user-info/" + body.id, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${user?.token}`,
          Aceept: "application/json",
        },
      });
      return await response.json();
    },
    onSuccess: (responseData) => {
      handleSendMessage(
        notifications,
        "راه ارتباطی حذف شد!",
        "success",
        3000,
        "delete"
      );

      JSON.stringify(responseData);
      refetchUser();
    },
    onError: (error) => {
      handleSendMessage(
        notifications,
        "خطا در عملیات حذف راه های ارتباطی!",
        "error",
        3000,
        "error"
      );
      console.log(error);
    },
  });

  const { mutateAsync: updateUserInfoAsync, isPending: isPendingUserInfoEdit } =
    useMutation({
      mutationFn: async (body: IUserInfo) => {
        const response = await fetch(API_URL + "user-info/" + body.id, {
          method: "Put",
          headers: {
            Accept: "*/*",
            "Content-Type": "application/json-patch+json",
            Authorization: `Bearer ${user?.token}`,
          },
          body: JSON.stringify(body),
        });

        return await response.json();
      },
      onSuccess: (responseData) => {
        JSON.stringify(responseData);
        handleSendMessage(
          notifications,
          "راه ارتباطی بروز رسانی شد!",
          "success",
          3000,
          "update"
        );
        refetchUser();
      },
      onError: (error) => {
        handleSendMessage(
          notifications,
          "خطا در عملیات همگام‌سازی راه های ارتباطی!",
          "error",
          3000,
          "error"
        );
        console.log(error);
      },
    });

  const onSave = async () => {
    if (
      (existingUserInfo?.length === 0 || !existingUserInfo) &&
      updatingUserInfo?.length === 0
    ) {
      handleSendMessage(
        notifications,
        "هیچ حساب بانکی برای ذخیره سازی وجود ندارد",
        "error",
        3000,
        "error"
      );
      return;
    }
    try {
      await syncItems<IUserInfo>({
        deleteItemAsync: deleteUserInfoAsync,
        existingItems: existingUserInfo,
        insertItemAsync: insertUserInfoAsync,
        isEdit: true,
        RefField: "createId",
        RefValue: userRef,
        updatedItems: updatingUserInfo,
        updateItemAsync: updateUserInfoAsync,
      });
    } catch (error) {
      console.error("خطا در همگام‌سازی کلی:", error);

      handleSendMessage(
        notifications,
        "خطا در عملیات همگام‌سازی راه های ارتباطی!",
        "error",
        3000,
        "error"
      );
    }
  };
  const onSaveAndClose = async () => {
    await onSave(); // منتظر تکمیل onSave
    setUpdatingUserInfo && setUpdatingUserInfo([]);
    onClose(); //
  };
  return (
    <GridToolbarContainer>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        width="100%"
        sx={{ padding: 1 }}
      >
        <Box>
          <Button color="primary" startIcon={<AddIcon />} onClick={handleClick}>
            راه ارتباطی جدید
          </Button>
        </Box>
        <ActionButtons
          onSave={onSave}
          onSaveAndClose={onSaveAndClose}
          isLoading={
            isPendingUserInfoEdit || isDeleteUserInfoLoading || userInfoLoading
          }
        />
      </Stack>
    </GridToolbarContainer>
  );
}
